// =============================================================================
// GOUVERNANCE - Gamora (agent cohérence des données TEF)
// =============================================================================
// Affiche le rapport complet (super_admin only).
// Endpoints :
//   POST /api/governance/gamora/run     — run manuel + email
//   GET  /api/governance/gamora/latest  — dernier rapport
//   GET  /api/governance/gamora/list    — historique
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { Shield, RefreshCw, Play, ChevronDown, ChevronRight, Mail } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";
import { getTenantHeaders } from "../../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL || "";

const SEVERITY_META = {
  critique: { color: "#dc2626", emoji: "🔴", label: "Critique" },
  grave:    { color: "#ea580c", emoji: "🟠", label: "Grave" },
  modere:   { color: "#ca8a04", emoji: "🟡", label: "Modéré" },
  mineur:   { color: "#16a34a", emoji: "🟢", label: "Mineur" },
};


export default function GouvGamora() {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const headers = useCallback(() => ({
    ...getTenantHeaders(), "Content-Type": "application/json",
  }), []);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const [latestRes, listRes] = await Promise.all([
        fetch(`${API}/api/governance/gamora/latest`, { headers: headers(), credentials: "include" }),
        fetch(`${API}/api/governance/gamora/list?limit=10`, { headers: headers(), credentials: "include" }),
      ]);
      if (latestRes.ok) {
        const data = await latestRes.json();
        setReport(data.report || null);
      }
      if (listRes.ok) {
        const data = await listRes.json();
        setHistory(data.reports || []);
      }
    } catch (e) {
      toast.error("Erreur chargement Gamora : " + e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/governance/gamora/run`, {
        method: "POST", headers: headers(), credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`Run terminé en ${data.duration_ms}ms — ${data.totals.critique}🔴 ${data.totals.grave}🟠 ${data.totals.modere}🟡`);
      await loadLatest();
    } catch (e) {
      toast.error("Erreur run : " + e.message);
    } finally {
      setRunning(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5" style={{ color: "var(--oh-stat-purple-text)" }} />
          <h2 className="text-lg font-semibold" style={{ color: "var(--oh-text)" }}>
            Gamora — Agent cohérence des données
          </h2>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={loadLatest} disabled={loading}
            data-testid="gamora-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
          <Button size="sm" onClick={runNow} disabled={running}
            className="bg-purple-600 hover:bg-purple-700" data-testid="gamora-run-now">
            <Play className="w-4 h-4 mr-1" />
            {running ? "Run en cours..." : "Run maintenant"}
          </Button>
        </div>
      </div>

      {report ? (
        <>
          <div className="rounded-xl p-4" style={{
            background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)",
          }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>
                  Dernier run
                </div>
                <div className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
                  {fmtDate(report.run_at)} · {report.duration_ms} ms
                </div>
              </div>
              <Mail className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(SEVERITY_META).map(([sev, meta]) => {
                const n = (report.totals || {})[sev] || 0;
                return (
                  <div key={sev} className="rounded-lg p-3 text-center"
                    style={{
                      background: n > 0 ? `${meta.color}15` : "var(--oh-bg-surface)",
                      border: `1px solid ${n > 0 ? meta.color + "40" : "var(--oh-border)"}`,
                    }}
                    data-testid={`gamora-total-${sev}`}>
                    <div className="text-2xl font-bold" style={{ color: n > 0 ? meta.color : "var(--oh-text-muted)" }}>
                      {meta.emoji} {n}
                    </div>
                    <div className="text-xs uppercase tracking-wide mt-1" style={{ color: "var(--oh-text-muted)" }}>
                      {meta.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            {(report.checks || []).map((c) => {
              const meta = SEVERITY_META[c.severity] || SEVERITY_META.mineur;
              const isOpen = !!expanded[c.id];
              return (
                <div key={c.id} className="rounded-lg" style={{
                  background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)",
                }} data-testid={`gamora-check-${c.id}`}>
                  <button
                    onClick={() => setExpanded(p => ({ ...p, [c.id]: !p[c.id] }))}
                    className="w-full flex items-center justify-between p-3 hover:opacity-80">
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span style={{ color: meta.color, fontWeight: 600 }}>{meta.emoji}</span>
                      <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
                        {c.label}
                      </span>
                      <Badge style={{ background: "var(--oh-bg-2)", color: "var(--oh-text)" }}>
                        scope: {c.scope}
                      </Badge>
                    </div>
                    <Badge style={{
                      background: c.count > 0 ? meta.color + "20" : "var(--oh-bg-2)",
                      color: c.count > 0 ? meta.color : "var(--oh-text-muted)",
                      fontWeight: 600,
                    }}>
                      {c.count} items
                    </Badge>
                  </button>
                  {isOpen && (c.items || []).length > 0 && (
                    <div className="border-t p-3 space-y-1" style={{ borderColor: "var(--oh-border)" }}>
                      {c.items.slice(0, 50).map((it, i) => (
                        <div key={i} className="text-xs flex items-start gap-2 py-1"
                          style={{ color: "var(--oh-text-secondary)" }}>
                          <span style={{ color: "var(--oh-text-muted)" }}>•</span>
                          <span>
                            <strong>{it.ref || it.dossier_id || it.client_id || it.doc_id}</strong>
                            {it.evidence && (
                              <span style={{ marginLeft: 8, opacity: 0.75 }}>
                                {Object.entries(it.evidence).map(([k, v]) => (
                                  <code key={k} style={{ marginRight: 8, fontSize: 11 }}>
                                    {k}={typeof v === "object" ? JSON.stringify(v) : String(v)}
                                  </code>
                                ))}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      {c.items.length > 50 && (
                        <div className="text-xs italic" style={{ color: "var(--oh-text-muted)" }}>
                          … +{c.items.length - 50} autres items
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {history.length > 1 && (
            <div className="rounded-lg p-3" style={{
              background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)",
            }}>
              <div className="text-xs uppercase tracking-wide mb-2" style={{ color: "var(--oh-text-muted)" }}>
                Historique (10 derniers runs)
              </div>
              <div className="space-y-1">
                {history.map((r) => (
                  <div key={r._id} className="text-xs flex items-center justify-between py-1"
                    style={{ color: "var(--oh-text-secondary)" }}>
                    <span>{fmtDate(r.run_at)}</span>
                    <span>
                      🔴{r.totals?.critique || 0} · 🟠{r.totals?.grave || 0} · 🟡{r.totals?.modere || 0} · 🟢{r.totals?.mineur || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg p-6 text-center text-sm" style={{
          color: "var(--oh-text-muted)", background: "var(--oh-bg-surface)",
          border: "1px dashed var(--oh-border)",
        }}>
          Aucun rapport encore. Cliquer sur "Run maintenant" pour démarrer.
        </div>
      )}
    </div>
  );
}
